/**
 * Network Status Component
 * Monitors network connectivity and API health
 * Shows user-friendly alerts when endpoints are disconnected
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

interface NetworkStatusProps {
  apiBaseUrl?: string;
  checkInterval?: number; // milliseconds
}

const NetworkStatus: React.FC<NetworkStatusProps> = ({
  apiBaseUrl = '/api',
  checkInterval = 30000 // Check every 30 seconds
}) => {
  const { t } = useTranslation('common');
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);
  const [apiHealth, setApiHealth] = useState<'healthy' | 'degraded' | 'down'>('healthy');
  const [lastError, setLastError] = useState<string>('');
  const [showAlert, setShowAlert] = useState<boolean>(false);

  /**
   * Check API health
   */
  const checkApiHealth = async (): Promise<void> => {
    try {
      const response = await axios.get(`${apiBaseUrl}/health`, {
        timeout: 5000
      });

      if (response.status === 200) {
        setApiHealth('healthy');
        setLastError('');
        setShowAlert(false);
      } else {
        setApiHealth('degraded');
        setLastError(`API returned status ${response.status}`);
        setShowAlert(true);
      }
    } catch (error: any) {
      console.error('[NetworkStatus] API health check failed:', error);

      setApiHealth('down');

      if (error.code === 'ECONNABORTED') {
        setLastError(t('network.apiTimedOut'));
      } else if (error.response) {
        setLastError(`${error.response.status} ${error.response.statusText}`);
      } else if (error.request) {
        setLastError(t('network.cannotReachApi'));
      } else {
        setLastError(t('network.healthCheckFailed'));
      }

      setShowAlert(true);
    }
  };

  /**
   * Handle online/offline events
   */
  useEffect(() => {
    const handleOnline = (): void => {
      console.log('[NetworkStatus] Network connection restored');
      setIsOnline(true);
      checkApiHealth();
    };

    const handleOffline = (): void => {
      console.log('[NetworkStatus] Network connection lost');
      setIsOnline(false);
      setShowAlert(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Initial health check
    checkApiHealth();

    // Set up periodic health checks
    const intervalId = setInterval(checkApiHealth, checkInterval);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(intervalId);
    };
  }, [apiBaseUrl, checkInterval]);

  /**
   * Auto-dismiss alert after 10 seconds if connection is restored
   */
  useEffect(() => {
    if (isOnline && apiHealth === 'healthy') {
      const timer = setTimeout(() => {
        setShowAlert(false);
      }, 10000);

      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isOnline, apiHealth]);

  /**
   * Retry connection
   */
  const handleRetry = async (): Promise<void> => {
    setShowAlert(false);
    await checkApiHealth();
  };

  /**
   * Don't show anything if everything is healthy
   */
  if (!showAlert || (isOnline && apiHealth === 'healthy')) {
    return null;
  }

  return (
    <div className="fixed top-20 md:top-24 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 animate-slide-down">
      <div
        className={`rounded-lg shadow-lg p-4 ${
          !isOnline || apiHealth === 'down'
            ? 'bg-red-500 text-white'
            : 'bg-yellow-500 text-white'
        }`}
      >
        <div className="flex items-start gap-3">
          {/* Icon */}
          <div className="flex-shrink-0">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {!isOnline || apiHealth === 'down' ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M18.364 5.636a9 9 0 010 12.728m0 0l-2.829-2.829m2.829 2.829L21 21M15.536 8.464a5 5 0 010 7.072m0 0l-2.829-2.829m-4.243 2.829a4.978 4.978 0 01-1.414-2.83m-1.414 5.658a9 9 0 01-2.167-9.238m7.824 2.167a1 1 0 111.414 1.414m-1.414-1.414L3 3m8.293 8.293l1.414 1.414"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              )}
            </svg>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-sm mb-1">
              {!isOnline
                ? t('network.noInternet')
                : apiHealth === 'down'
                ? t('network.apiUnavailable')
                : t('network.connectionIssues')}
            </h3>
            <p className="text-xs opacity-90">
              {!isOnline
                ? t('network.checkConnection')
                : lastError || t('network.someFeaturesUnavailable')}
            </p>
          </div>

          {/* Close button */}
          <button
            onClick={() => setShowAlert(false)}
            className="flex-shrink-0 p-1 hover:bg-white/20 rounded transition-colors"
            aria-label={t('network.dismiss')}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Retry button */}
        {(apiHealth === 'down' || apiHealth === 'degraded') && (
          <button
            onClick={handleRetry}
            className="mt-3 w-full bg-white/20 hover:bg-white/30 text-white text-xs font-medium py-2 px-3 rounded transition-colors"
          >
            {t('network.retryConnection')}
          </button>
        )}
      </div>
    </div>
  );
};

export default NetworkStatus;
